import { GrpcWebFetchTransport } from "@protobuf-ts/grpcweb-transport"
import type { RpcInterceptor, RpcTransport } from "@protobuf-ts/runtime-rpc"
import type { TransportOptions } from "./types"

// Re-export for convenience
export type { RpcInterceptor }

/**
 * Interceptor that normalizes every request through its message type's
 * `create()` before serialization. Callers (pages, generated stores) often pass
 * partial request objects; protobuf-ts's binary writer does not tolerate a
 * missing repeated or map field and throws. `create()` fills every omitted
 * field with its proto3 default while keeping supplied values (including
 * explicit `false`, `0` and `0n`). It runs first, so caller-supplied
 * interceptors always see a complete message.
 */
export const fillRequestDefaults: RpcInterceptor = {
  interceptUnary(next, method, input, options) {
    return next(method, method.I.create(input as never), options)
  },
  interceptServerStreaming(next, method, input, options) {
    return next(method, method.I.create(input as never), options)
  },
}

/**
 * Create a configured gRPC-Web transport
 *
 * @example
 * ```ts
 * const transport = createTransport({
 *   baseUrl: process.env.NEXT_PUBLIC_GRPC_ENDPOINT!,
 *   timeout: 30000,
 *   headers: { 'x-api-key': 'secret' }
 * })
 * ```
 *
 * For authentication, pass a custom interceptor:
 * @example
 * ```ts
 * const authInterceptor: RpcInterceptor = {
 *   interceptUnary(next, method, input, options) {
 *     if (!options.meta) options.meta = {}
 *     options.meta['Authorization'] = `Bearer ${getToken()}`
 *     return next(method, input, options)
 *   }
 * }
 *
 * const transport = createTransport({
 *   baseUrl: process.env.NEXT_PUBLIC_GRPC_ENDPOINT!,
 *   interceptors: [authInterceptor]
 * })
 * ```
 */
export function createTransport(options: TransportOptions): RpcTransport {
  const { baseUrl, timeout = 30000, headers = {}, interceptors = [] } = options

  return new GrpcWebFetchTransport({
    baseUrl,
    format: "binary",
    timeout,
    meta: headers,
    interceptors: [fillRequestDefaults, ...interceptors],
  })
}

/**
 * Create a gRPC-Web transport for long-lived *server-streaming* RPCs.
 *
 * Unlike {@link createTransport}, this sets NO deadline. In @protobuf-ts the
 * `timeout` option is emitted as the `grpc-timeout` request header (a
 * server-side deadline), and any value <= 0 throws — so there is no way to
 * express "no deadline" through `timeout`. A finite deadline would cancel a
 * live stream server-side once it elapses (e.g. the 30s default would kill the
 * stream every 30s). Omitting it lets the stream run until the server's own
 * max-lifetime close or a client abort.
 *
 * Use this ONLY for streaming endpoints; unary calls should keep a timeout.
 */
export function createStreamingTransport(options: Omit<TransportOptions, "timeout">): RpcTransport {
  const { baseUrl, headers = {}, interceptors = [] } = options

  return new GrpcWebFetchTransport({
    baseUrl,
    format: "binary",
    meta: headers,
    interceptors: [fillRequestDefaults, ...interceptors],
  })
}

/**
 * Default transport instance using environment variable
 * Only use this if you have a single gRPC endpoint
 */
let defaultTransport: RpcTransport | null = null

/**
 * Override the singleton default transport used by generated clients.
 * This is the intended way to inject interceptors (e.g. Authorization) at app startup.
 */
export function setDefaultTransport(transport: RpcTransport): void {
  defaultTransport = transport
}

export function getDefaultTransport(): RpcTransport {
  if (!defaultTransport) {
    let baseUrl: string | undefined

    // Check Vite environment variables first (import.meta.env)
    if (typeof import.meta !== "undefined" && import.meta.env) {
      baseUrl = import.meta.env.VITE_GRPC_ENDPOINT
    }

    // Fallback to Next.js or CRA environment variables
    if (!baseUrl && typeof process !== "undefined") {
      baseUrl = process.env.NEXT_PUBLIC_GRPC_ENDPOINT || process.env.REACT_APP_GRPC_ENDPOINT
    }

    if (!baseUrl) {
      throw new Error(
        "No gRPC endpoint configured. Set VITE_GRPC_ENDPOINT, NEXT_PUBLIC_GRPC_ENDPOINT, or REACT_APP_GRPC_ENDPOINT, " +
          "or create a transport manually with createTransport()"
      )
    }

    defaultTransport = createTransport({ baseUrl })
  }

  return defaultTransport
}

/**
 * Reset default transport (useful for testing)
 */
export function resetDefaultTransport(): void {
  defaultTransport = null
}
